import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Button,
    Box,
    Card,
    Typography,
    Stack,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Paper,
    TextField,
    IconButton,
    Toolbar,
    InputAdornment,
    Collapse,
    Avatar
} from '@mui/material';
import {
    Add,
    Delete,
    Search,
    FilterList,
    ExpandMore,
    ExpandLess,
    LocationOn
} from '@mui/icons-material';
import dealerApi from '../../../../services/dealerApi';
import { BUTTON_SAVE_COLOR } from '../../../common/color';

const DealerIndex = () => {
    const [dealers, setDealers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [searchTerm, setSearchTerm] = useState('');
    const [expandedDzongkhags, setExpandedDzongkhags] = useState({});
    const navigate = useNavigate();

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const response = await dealerApi.getDealersByDzongkhag();
                setDealers(response);
                
                const initialExpanded = {};
                response.forEach(dzongkhag => {
                    initialExpanded[dzongkhag.id] = true;
                });
                setExpandedDzongkhags(initialExpanded);
            } catch (error) {
                console.error('Fetch error:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    const handleDelete = async (dealerId) => {
        try {
            await dealerApi.delete(dealerId);
            setDealers(prev => prev.map(dzongkhag => ({
                ...dzongkhag,
                dealers: dzongkhag.dealers.filter(dealer => dealer.id !== dealerId)
            })));
        } catch (error) {
            console.error('Error deleting dealer:', error);
        }
    };

    const handleToggleDzongkhag = (dzongkhagId) => {
        setExpandedDzongkhags(prev => ({
            ...prev,
            [dzongkhagId]: !prev[dzongkhagId]
        }));
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleSearch = (event) => {
        setSearchTerm(event.target.value);
        setPage(0);
    };

    const filteredDealers = dealers.map(dzongkhag => {
        if (!searchTerm) return dzongkhag;
        
        return {
            ...dzongkhag,
            dealers: dzongkhag.dealers.filter(dealer =>
                dealer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                (dealer.contactPerson && dealer.contactPerson.toLowerCase().includes(searchTerm.toLowerCase())) ||
                (dealer.phone && dealer.phone.includes(searchTerm)) ||
                (dealer.email && dealer.email.toLowerCase().includes(searchTerm.toLowerCase())
            )
)};
    }).filter(dzongkhag => dzongkhag.dealers.length > 0);

    const columns = [
        {
            id: 'name',
            label: 'Dealer Name',
            width: '200px',
            sortable: false
        },
        {
            id: 'contactPerson',
            label: 'Contact Person',
            width: '150px',
            sortable: false
        },
        {
            id: 'phone',
            label: 'Phone',
            width: '120px',
            sortable: false
        },
        {
            id: 'email',
            label: 'Email',
            width: '200px',
            sortable: false
        },
        {
            id: 'actions',
            label: 'Actions',
            width: '120px',
            align: 'center',
            sortable: false,
            format: (value, row) => (
                <>
                    <IconButton
                        aria-label="delete"
                        onClick={() => handleDelete(row.id)}
                        size="small"
                    >
                        <Delete fontSize="small" />
                    </IconButton>
                </>
            )
        }
    ];

    return (
        <Box sx={{ width: '100%' }}>
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                mb={3}
                sx={{
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    gap: { xs: 2, sm: 0 }
                }}
            >
                <Typography
                    variant="h4"
                    sx={{
                        fontSize: { xs: '1.5rem', sm: '1.7rem' },
                        fontWeight: 600
                    }}
                >
                    Dealer Management
                </Typography>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<Add />}
                    onClick={() => navigate('/admin/dealers/new')}
                    sx={{
                        boxShadow: 'none',
                        textTransform: 'capitalize',
                        borderRadius: '6px',
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        padding: { xs: '6px 12px', sm: '8px 16px' },
                        background: BUTTON_SAVE_COLOR
                    }}
                >
                    Add New Dealer
                </Button>
            </Stack>

            <Card sx={{
                p: 2,
                boxShadow: 'rgba(145, 158, 171, 0.2) 0px 0px 2px 0px, rgba(145, 158, 171, 0.12) 0px 12px 24px -4px',
                borderRadius: '12px'
            }}>
                <Paper sx={{ width: '100%', mb: 2 }}>
                    <Toolbar
                        sx={{
                            pl: { sm: 2 },
                            pr: { xs: 1, sm: 1 },
                        }}
                    >
                        <TextField
                            variant="outlined"
                            size="small"
                            placeholder="Search dealers..."
                            value={searchTerm}
                            onChange={handleSearch}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Search />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{
                                width: '300px',
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '6px'
                                }
                            }}
                        />
                        <Box sx={{ flex: '1 1 100%' }} />
                        <IconButton>
                            <FilterList />
                        </IconButton>
                    </Toolbar>

                    <TableContainer>
                        <Table
                            sx={{ minWidth: 750 }}
                            aria-labelledby="tableTitle"
                            size={'medium'}
                        >
                            <TableHead>
                                <TableRow>
                                    <TableCell width="60px" />
                                    <TableCell
                                        width="300px"
                                        sx={{
                                            fontWeight: 600,
                                            color: 'text.secondary',
                                            backgroundColor: 'background.paper',
                                            borderBottom: '1px solid',
                                            borderColor: 'divider'
                                        }}
                                    >
                                        Dzongkhag
                                    </TableCell>
                                    <TableCell
                                        width="100px"
                                        align="center"
                                        sx={{
                                            fontWeight: 600,
                                            color: 'text.secondary',
                                            backgroundColor: 'background.paper',
                                            borderBottom: '1px solid',
                                            borderColor: 'divider'
                                        }}
                                    >
                                        Dealers Count
                                    </TableCell>
                                    <TableCell
                                        width="100px"
                                        align="center"
                                        sx={{
                                            fontWeight: 600,
                                            color: 'text.secondary',
                                            backgroundColor: 'background.paper',
                                            borderBottom: '1px solid',
                                            borderColor: 'divider'
                                        }}
                                    >
                                        Actions
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? (
                                    <TableRow>
                                        <TableCell colSpan={4} align="center">
                                            Loading...
                                        </TableCell>
                                    </TableRow>
                                ) : filteredDealers.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={4} align="center">
                                            No dealers found
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    filteredDealers.map((dzongkhag) => (
                                        <React.Fragment key={dzongkhag.id}>
                                            <TableRow
                                                hover
                                                sx={{
                                                    '& .MuiTableCell-root': {
                                                        borderBottom: '1px dashed',
                                                        borderColor: 'divider'
                                                    }
                                                }}
                                            >
                                                <TableCell>
                                                    <IconButton
                                                        aria-label="expand row"
                                                        size="small"
                                                        onClick={() => handleToggleDzongkhag(dzongkhag.id)}
                                                    >
                                                        {expandedDzongkhags[dzongkhag.id] ? (
                                                            <ExpandLess />
                                                        ) : (
                                                            <ExpandMore />
                                                        )}
                                                    </IconButton>
                                                </TableCell>
                                                <TableCell>
                                                    <Stack direction="row" alignItems="center" spacing={2}>
                                                        <Avatar sx={{ bgcolor: '#e3f2fd' }}>
                                                            <LocationOn color="primary" />
                                                        </Avatar>
                                                        <Typography variant="subtitle1">
                                                            {dzongkhag.name}
                                                        </Typography>
                                                    </Stack>
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Chip 
                                                        label={dzongkhag.dealers.length} 
                                                        color="primary" 
                                                        size="small"
                                                    />
                                                </TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell colSpan={4} sx={{ p: 0 }}>
                                                    <Collapse in={expandedDzongkhags[dzongkhag.id]} timeout="auto" unmountOnExit>
                                                        <Box sx={{ margin: 1 }}>
                                                            <Table size="small" aria-label="dealers">
                                                                <TableHead>
                                                                    <TableRow>
                                                                        {columns.map((column) => (
                                                                            <TableCell
                                                                                key={column.id}
                                                                                align={column.align || 'left'}
                                                                                width={column.width}
                                                                                sx={{
                                                                                    fontWeight: 600,
                                                                                    color: 'text.secondary',
                                                                                    backgroundColor: '#f9f9f9',
                                                                                    borderBottom: '1px solid',
                                                                                    borderColor: 'divider'
                                                                                }}
                                                                            >
                                                                                {column.label}
                                                                            </TableCell>
                                                                        ))}
                                                                    </TableRow>
                                                                </TableHead>
                                                                <TableBody>
                                                                    {dzongkhag.dealers.map((dealer) => (
                                                                        <TableRow key={dealer.id} hover>
                                                                            {columns.map((column) => {
                                                                                const value = dealer[column.id];
                                                                                return (
                                                                                    <TableCell
                                                                                        key={column.id}
                                                                                        align={column.align}
                                                                                    >
                                                                                        {column.format
                                                                                            ? column.format(value, dealer)
                                                                                            : (
                                                                                                <Typography variant="body2">
                                                                                                    {value || '-'}
                                                                                                </Typography>
                                                                                            )
                                                                                        }
                                                                                    </TableCell>
                                                                                );
                                                                            })}
                                                                        </TableRow>
                                                                    ))}
                                                                </TableBody>
                                                            </Table>
                                                        </Box>
                                                    </Collapse>
                                                </TableCell>
                                            </TableRow>
                                        </React.Fragment>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        rowsPerPageOptions={[5, 10, 20, 50]}
                        component="div"
                        count={filteredDealers.reduce((sum, dzongkhag) => sum + dzongkhag.dealers.length, 0)}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                </Paper>
            </Card>
        </Box>
    );
};

export default DealerIndex;